import React from 'react';
import { StatusPanel, ChartPanel, MinersPanel, EarningsPanel } from '../panel';
import { useMiningDataManager } from '../../hooks/useMiningDataManager';
import ConnectionNotification from '../common/ConnectionNotification';

const Dashboard: React.FC = () => {
  const miningData = useMiningDataManager();

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-950">
      {/* Sub-header: title, controls, and the primary KPI strip */}
      <div className="sticky short:static top-16 z-40 bg-gray-50/85 dark:bg-slate-950/85 backdrop-blur-md border-b border-gray-200 dark:border-slate-800 mb-6 short:mb-3">
        <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 py-3 short:py-2">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3 short:mb-1.5">
            <div>
              <h1 className="text-xl short:text-base font-bold tracking-tight text-gray-900 dark:text-white">
                Mining Dashboard
              </h1>
              <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5 short:hidden">
                Real-time mining operations overview
              </p>
            </div>

            {/* Status Indicators */}
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <div className={`w-2 h-2 rounded-full ${
                  !miningData.isLoading ? 'bg-emerald-500' : 'bg-amber-500 animate-pulse'
                }`}></div>
                <span className="text-xs text-gray-500 dark:text-slate-400">
                  {!miningData.isLoading ? 'Services Active' : 'Initializing...'}
                </span>
              </div>

              <div className="flex items-center space-x-2">
                {/* Refresh Button */}
                <button
                  onClick={miningData.refreshData}
                  disabled={miningData.isFetching}
                  className="px-4 py-2.5 sm:px-3 sm:py-1.5 short:py-1 text-xs font-medium bg-red-600 hover:bg-red-700 disabled:bg-gray-400 dark:disabled:bg-slate-700 text-white rounded-lg flex items-center space-x-1.5"
                >
                  <svg className={`w-3 h-3 ${miningData.isFetching ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  <span>{miningData.isFetching ? 'Refreshing...' : 'Refresh'}</span>
                </button>
              </div>
            </div>
          </div>

          {/* Primary KPIs — these are the headline facts of the operation */}
          {!miningData.isLoading && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-2 short:gap-y-1">
              <div>
                <p className="stat-label">API Status</p>
                <p className="flex items-center gap-1.5 text-base short:text-sm font-bold text-gray-900 dark:text-white capitalize leading-snug">
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
                    miningData.apiStatus === 'connected' ? 'bg-emerald-500' :
                    miningData.apiStatus === 'error' ? 'bg-red-500' : 'bg-amber-500'
                  }`}></span>
                  {miningData.apiStatus}
                </p>
              </div>
              <div>
                <p className="stat-label">Online Miners</p>
                <p className="text-base short:text-sm font-bold tabular-nums text-gray-900 dark:text-white leading-snug">
                  {miningData.derivedStats.onlineMinersCount}
                </p>
              </div>
              <div>
                <p className="stat-label">Total Workers</p>
                <p className="text-base short:text-sm font-bold tabular-nums text-gray-900 dark:text-white leading-snug">
                  {miningData.dashboardStats?.totalWorkers || 0}
                </p>
              </div>
              {miningData.lastUpdate > 0 && (
                <div>
                  <p className="stat-label">Last Update</p>
                  <p className="text-base short:text-sm font-bold tabular-nums text-gray-900 dark:text-white leading-snug">
                    {new Date(miningData.lastUpdate).toLocaleTimeString()}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Error Display */}
          {miningData.error && (
            <div className="mt-3 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <svg className="w-4 h-4 text-red-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="text-sm text-red-700 dark:text-red-300">{miningData.error}</span>
                </div>
              </div>
              {miningData.timeUntilRetry > 0 && (
                <div className="mt-2 text-xs tabular-nums text-red-600 dark:text-red-400">
                  Next retry in {Math.ceil(miningData.timeUntilRetry / 1000)}s
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Main Dashboard Grid - Responsive Layout */}
      <div className="max-w-[1800px] mx-auto px-3 sm:px-4 lg:px-6 xl:px-8 pb-[max(1.5rem,env(safe-area-inset-bottom))] short:pb-3">
        {/* Earnings — the money band leads the page. Full-width so the pixel
            numerals get room on every screen, one horizontal row on short-xl
            (1920x550) so it costs minimal height there. */}
        <section className="mb-6 lg:mb-8 short:mb-3">
          <h2 className="section-label mb-2 short:mb-1.5">Earnings</h2>
          <EarningsPanel />
        </section>

        {/* Top Row - Status, Performance, and Pool Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 lg:gap-6 short:gap-3 mb-6 lg:mb-8 short:mb-3">

          {/* Status Section */}
          <section>
            <h2 className="section-label mb-2 short:mb-1.5">Mining Status</h2>
            <StatusPanel
              stats={miningData.dashboardStats}
              loading={miningData.isLoading}
              getCachedCryptoProgress={miningData.getCachedCryptoProgress}
            />
          </section>

          {/* Performance Section */}
          <section>
            <h2 className="section-label mb-2 short:mb-1.5">Performance</h2>
            <ChartPanel
              loading={miningData.isLoading}
              getCachedAverageHashrate={miningData.getCachedAverageHashrate}
              getCachedCryptoProgress={miningData.getCachedCryptoProgress}
            />
          </section>

          {/* Pool Overview Section */}
          <section className="md:col-span-2 xl:col-span-1">
            <h2 className="section-label mb-2 short:mb-1.5">Pool Overview</h2>

            <div className="card p-4 lg:p-5 short:p-3">
              {/* Pool rows — flat list inside the one card */}
              <div className="divide-y divide-gray-100 dark:divide-slate-800">
                <div className="flex items-center justify-between py-2.5 short:py-1.5 first:pt-0">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-sky-100 dark:bg-sky-500/15 rounded-lg flex items-center justify-center">
                      <span className="text-sm font-bold text-sky-700 dark:text-sky-400">Ł</span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">Litecoin Pool</p>
                      <p className="text-[11px] text-gray-500 dark:text-slate-400">Scrypt Algorithm</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xl short:text-base font-bold tabular-nums text-gray-900 dark:text-white leading-tight">
                      {miningData.miners.filter(m => m.algorithm === 'Scrypt' && m.status === 'online').length}
                    </p>
                    <p className="text-[11px] text-gray-500 dark:text-slate-400">online</p>
                  </div>
                </div>

                <div className="flex items-center justify-between py-2.5 short:py-1.5">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-orange-100 dark:bg-orange-500/15 rounded-lg flex items-center justify-center">
                      <span className="text-sm font-bold text-orange-700 dark:text-orange-400">₿</span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">SHA256 Pool</p>
                      <p className="text-[11px] text-gray-500 dark:text-slate-400">Bitcoin Algorithm</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xl short:text-base font-bold tabular-nums text-gray-900 dark:text-white leading-tight">
                      {miningData.miners.filter(m => m.algorithm === 'SHA256' && m.status === 'online').length}
                    </p>
                    <p className="text-[11px] text-gray-500 dark:text-slate-400">online</p>
                  </div>
                </div>
              </div>

              {/* Online miners split — proportional, both sides in the same unit (miners) */}
              {(miningData.miners.filter(m => m.algorithm === 'Scrypt' && m.status === 'online').length +
                miningData.miners.filter(m => m.algorithm === 'SHA256' && m.status === 'online').length) > 0 && (
                <div className="mt-3 short:mt-2 flex h-1.5 rounded-full overflow-hidden gap-x-0.5" aria-hidden="true">
                  <div
                    className="bg-sky-600 rounded-full"
                    style={{ flexGrow: miningData.miners.filter(m => m.algorithm === 'Scrypt' && m.status === 'online').length }}
                  ></div>
                  <div
                    className="bg-orange-600 rounded-full"
                    style={{ flexGrow: miningData.miners.filter(m => m.algorithm === 'SHA256' && m.status === 'online').length }}
                  ></div>
                </div>
              )}

              {/* Hashrate Metrics */}
              <div className="mt-4 short:mt-2.5 pt-4 short:pt-2.5 border-t border-gray-100 dark:border-slate-800 space-y-2.5 short:space-y-1.5">
                <h3 className="stat-label">Hashrate by Pool</h3>

                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2 text-xs tall:text-sm text-gray-600 dark:text-slate-400">
                    <span className="w-2 h-2 rounded-full bg-sky-600 flex-shrink-0" aria-hidden="true"></span>
                    Litecoin (GH/s)
                  </span>
                  <span className="text-base tall:text-lg font-semibold tabular-nums text-gray-900 dark:text-white">
                    {((miningData.derivedStats.scryptHashrate/1000 || 0)).toFixed(3)}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2 text-xs tall:text-sm text-gray-600 dark:text-slate-400">
                    <span className="w-2 h-2 rounded-full bg-orange-600 flex-shrink-0" aria-hidden="true"></span>
                    SHA256 (TH/s)
                  </span>
                  <span className="text-base tall:text-lg font-semibold tabular-nums text-gray-900 dark:text-white">
                    {((miningData.derivedStats.sha256Hashrate || 0)).toFixed(3)}
                  </span>
                </div>
              </div>
            </div>
          </section>
        </div>

        {/* Bottom Row - Miners Section (Full Width) */}
        <section className="mb-4 lg:mb-6">
          <h2 className="section-label mb-2 short:mb-1.5">Mining Hardware</h2>
          <MinersPanel
            miners={miningData.miners}
            loading={miningData.isLoading}
          />
        </section>

        {/* Network Scanner Section - Hidden until functional */}
        {/* <NetworkScanner /> */}
      </div>

      {/* Floating Connection Notification */}
      <ConnectionNotification
        dataFreshness={miningData.dataFreshness}
        isFetching={miningData.isFetching}
        error={miningData.error}
        canRetry={miningData.canRetry}
        timeUntilRetry={miningData.timeUntilRetry}
        onRefresh={miningData.refreshData}
      />
    </div>
  );
};

export default Dashboard;
